import { createAction, createReducer } from '@reduxjs/toolkit'

import { USE_REDUX_TOOLS } from 'system'

import cloneDeep from 'lodash/cloneDeep'

import type { Draft, PayloadActionCreator } from '@reduxjs/toolkit'

const reduxCreatorReduxTools = <TName extends string, TPayload, TState extends object>(
  actionsPrefix: TName,
  initialState: TState,
  actionCallback?: (state: TState, action: { payload: TPayload; type: TName }) => TState | void,
) => {
  const actionStart = createAction<TPayload, TName>(actionsPrefix)

  const reducer = createReducer(initialState, (builder) =>
    builder.addCase(actionStart, (state, action) => {
      if (actionCallback) {
        // eslint-disable-next-line no-param-reassign
        state =
          (actionCallback(state as TState, action as { payload: TPayload; type: TName }) as Draft<TState>) || state
      }
      return state
    }),
  )

  return {
    [actionsPrefix]: actionStart,
    reducer,
  } as Record<'reducer', typeof reducer> & Record<TName, typeof actionStart>
}

export const actionCreator = <TPayload = void, TName extends string = string>(
  type: TName,
): PayloadActionCreator<TPayload, TName> => {
  const action = (payload: TPayload) => ({
    payload,
    type,
  })

  action.toString = () => `${type}`
  action.type = type
  action.match = (act: { type: string }): boolean => type === act.type

  return action as PayloadActionCreator<TPayload, TName>
}

const reduxCreatorRedux = <TName extends string, TPayload, TState extends object>(
  actionsPrefix: TName,
  initialState: TState,
  actionCallback?: (state: TState, action: { payload: TPayload; type: TName }) => TState | void,
) => {
  const actionStart = actionCreator<TPayload, TName>(actionsPrefix)

  const reducer = (state = initialState, action: { payload?: TPayload; type?: TName } = {}) => {
    switch (action.type) {
      case actionStart.type: {
        let newState = cloneDeep(state)
        if (actionCallback) {
          newState = actionCallback(newState as TState, action as { payload: TPayload; type: TName }) || newState
        }
        return newState
      }
      default: {
        return state
      }
    }
  }

  return {
    [actionsPrefix]: actionStart,
    reducer,
  } as Record<'reducer', typeof reducer> & Record<TName, typeof actionStart>
}

const reduxCreator = USE_REDUX_TOOLS ? reduxCreatorReduxTools : reduxCreatorRedux

export default reduxCreator
